import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AimTutorialOverlay } from "@/components/AimTutorialOverlay";
import { Pitch } from "@/components/Pitch";
import { ResultOverlay } from "@/components/ResultOverlay";
import { UserPill } from "@/components/UserPill";
import { ApiError, api } from "@/api/client";
import { useGameStore } from "@/store/gameStore";
import { puzzleLabel } from "@/lib/puzzleNumber";
import {
  formatCountdownString,
  formatRemaining,
  getCountdown,
} from "@/lib/worldCup";

function formatApiError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body;
    if (body && typeof body === "object" && "error" in body) {
      const errVal = (body as { error: unknown }).error;
      return `${err.status}: ${String(errVal)}`;
    }
    return `${err.status}: ${err.message}`;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Format an ISO date (YYYY-MM-DD) as "Sun, May 17, 2026" — month name first
 * to eliminate dd/mm vs mm/dd ambiguity for international users.
 */
function formatPuzzleDate(iso: string): string {
  const d = new Date(iso + "T00:00:00Z");
  if (Number.isNaN(d.getTime())) return iso;
  const month = d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  const weekday = d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
  const day = d.getUTCDate();
  const year = d.getUTCFullYear();
  return `${weekday}, ${month} ${day}, ${year}`;
}

interface CountdownTexts {
  /** Full phrase shown on desktop, e.g. "World Cup kickoff in 25d 04h". */
  long: string;
  /** Compact phrase with football glyph, shown on mobile so the narrow
   *  centre column doesn't truncate, e.g. "⚽ Kickoff in 25d 04h". */
  short: string;
}

/** Hook returning the World Cup kickoff/final countdown text in two forms
 *  (long for desktop, short for mobile). Updates every five minutes while
 *  >24h away (display only changes once an hour), every minute in the
 *  final day, every second in the final hour. Pauses when the tab is hidden. */
function useWorldCupCountdownText(): CountdownTexts | null {
  const [now, setNow] = useState<number>(() => Date.now());

  useEffect(() => {
    const { phase, remainingMs } = getCountdown(now);
    if (phase === "post") return;
    const intervalMs =
      remainingMs > 24 * 60 * 60 * 1000
        ? 5 * 60_000
        : remainingMs > 60 * 60 * 1000
          ? 60_000
          : 1_000;
    let id: number | null = null;
    const start = () => { if (id === null) id = window.setInterval(() => setNow(Date.now()), intervalMs); };
    const stop = () => { if (id !== null) { window.clearInterval(id); id = null; } };
    const onVis = () => {
      if (document.visibilityState === "visible") { setNow(Date.now()); start(); } else stop();
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [now]);

  const state = getCountdown(now);
  if (state.phase === "post" || !state.label) return null;
  const value = formatCountdownString(formatRemaining(state.remainingMs));
  const eventLong = state.label === "Kickoff" ? "World Cup kickoff in" : "World Cup final in";
  const eventShort = state.label === "Kickoff" ? "Kickoff in" : "Final in";
  return {
    long: `${eventLong} ${value}`,
    short: `⚽ ${eventShort} ${value}`,
  };
}

export function PlayPage() {
  const setPuzzle = useGameStore((s) => s.setPuzzle);
  const puzzle = useGameStore((s) => s.puzzle);
  const state = useGameStore((s) => s.state);
  const [error, setError] = useState<string | null>(null);
  const countdownText = useWorldCupCountdownText();

  useEffect(() => {
    let cancelled = false;
    api
      .todayPuzzle()
      .then((p) => { if (!cancelled) setPuzzle(p); })
      .catch((err: unknown) => { if (!cancelled) setError(formatApiError(err)); });
    return () => { cancelled = true; };
  }, [setPuzzle]);

  if (error) {
    return (
      <main className="flex min-h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-rose-300">Could not load today&rsquo;s puzzle.</p>
        <p className="text-sm opacity-60">{error}</p>
        <p className="text-xs opacity-50">
          If the dev server is not running, start it with{" "}
          <code className="rounded bg-white/10 px-2 py-0.5">npm run dev:server</code>
          {" in another terminal."}
        </p>
      </main>
    );
  }

  if (state === "LOADING" || !puzzle) {
    return (
      <main className="flex min-h-full flex-col items-center justify-center gap-2 p-6">
        <p className="opacity-70">Loading today&rsquo;s puzzle&hellip;</p>
      </main>
    );
  }

  return (
    <main className="relative flex h-full flex-col">
      {/*
        Three-column banner per design reference:
          [stadium / conditions]   [CURVLE / WC countdown]   [Curvle #N / date]
        Single horizontal strip — no second row. Action buttons (Leaderboard,
        country picker, sign-in pill) are absolute-positioned top-right OVER
        the canvas so they don't steal banner space.

        Mobile (<sm): banner text scales down; columns stay 3-up. Conditions
        truncate with ellipsis if very long.
      */}
      <header className="relative grid grid-cols-3 items-center gap-2 px-3 pt-3 pb-2 sm:px-4 sm:pt-4 sm:pb-3">
        {/* Col 1 — Stadium + conditions */}
        <div className="flex flex-col leading-tight">
          {puzzle.stadiumName && (
            <span className="truncate text-sm font-bold sm:text-base">
              {puzzle.stadiumName}
            </span>
          )}
          {puzzle.conditionsLabel && (
            <span className="truncate text-[11px] opacity-70 sm:text-xs">
              {puzzle.conditionsLabel}
            </span>
          )}
        </div>

        {/* Col 2 — CURVLE wordmark + World Cup countdown.
            Countdown has two variants:
              - Mobile (<sm): "⚽ Kickoff in 25d 04h"  — compact, fits a
                ~120px column on a 390px iPhone without truncating.
              - Desktop (≥sm): "World Cup kickoff in 25d 04h" — full
                phrase, more inviting in the wide hero strip.
            Both share the same Tailwind classes for size + opacity. */}
        <div className="flex min-w-0 flex-col items-center text-center leading-tight">
          <span className="text-base font-extrabold uppercase tracking-widest sm:text-xl">
            Curvle
          </span>
          {countdownText && (
            <>
              <span className="max-w-full truncate text-[11px] opacity-70 sm:hidden">
                {countdownText.short}
              </span>
              <span className="hidden max-w-full truncate text-xs opacity-70 sm:block">
                {countdownText.long}
              </span>
            </>
          )}
        </div>

        {/* Col 3 — Puzzle number + date */}
        <div className="flex flex-col items-end leading-tight">
          <span className="text-sm font-bold sm:text-base">
            {puzzleLabel(puzzle.date)}
          </span>
          <span className="truncate text-[11px] opacity-70 sm:text-xs">
            {formatPuzzleDate(puzzle.date)}
          </span>
        </div>
      </header>

      <div className="relative flex-1">
        <Pitch params={puzzle.params} puzzleId={puzzle.puzzleId} />
        <ResultOverlay />
        {/* Tutorial only fires when the user is actually about to aim — not
            when they've already played today (RESULT state on mount, via
            restoreExistingAttempt). Returning players don't need a tutorial
            on a result screen they're just reading. */}
        <AimTutorialOverlay ready={state === "AIMING"} />

        {/*
          Action toolbar — absolute over the canvas top-right. Stays out of
          the banner so the banner can use the full row for content.
          On mobile we shrink the Leaderboard label to an icon to keep
          the toolbar narrow.
        */}
        <div className="pointer-events-none absolute right-2 top-2 z-10 flex items-center gap-1.5 sm:right-3 sm:top-3 sm:gap-2">
          <Link
            to="/leaderboard"
            className="pointer-events-auto rounded-full bg-black/40 px-3 py-1.5 text-xs font-semibold backdrop-blur-sm hover:bg-black/55"
            aria-label="Leaderboard"
          >
            <span className="hidden sm:inline">Leaderboard</span>
            <span aria-hidden="true" className="sm:hidden">🏆</span>
          </Link>
          <div className="pointer-events-auto">
            <UserPill />
          </div>
        </div>
      </div>
    </main>
  );
}
